const sanitizeName = (value) => {
  if (value === null || value === undefined) return null;
  const str = String(value).trim();
  return str || null;
};

const onlyDigits = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/\D/g, '');
};

const normalizePhoneDigits = (value) => {
  const normalized = sanitizeName(value);
  if (!normalized) return '';
  const cleaned = normalized.replace(/^whatsapp:/i, '').replace(/@.+$/, '');
  return onlyDigits(cleaned);
};

const collectContacts = (entry) => {
  const contacts = [];
  if (!entry) return contacts;
  const primaryContact = getConversationContact(entry);
  if (primaryContact) contacts.push(primaryContact);
  if (Array.isArray(entry.contacts)) contacts.push(...entry.contacts);
  return contacts;
};

const pushIfValid = (list, value) => {
  const sanitized = sanitizeName(value);
  if (sanitized) {
    list.push(sanitized);
  }
};

const pushPhoneIfValid = (set, value) => {
  const digits = normalizePhoneDigits(value);
  if (digits) {
    set.add(digits);
  }
};

const matchesKnownPhone = (candidate, knownPhones) => {
  const digits = onlyDigits(candidate);
  if (!digits) return false;
  if (knownPhones.has(digits)) return true;
  for (const phone of knownPhones) {
    if (phone.endsWith(digits) || digits.endsWith(phone)) {
      return true;
    }
  }
  return false;
};

const findBestCandidate = (candidates, knownPhones) =>
  candidates
    .map((candidate) => sanitizeName(candidate))
    .find((candidate) => candidate && !matchesKnownPhone(candidate, knownPhones)) || null;

/**
 * Resolve o nome mais informativo possível de um contato/slot,
 * priorizando nome curado/cadastral, depois notify/verified/push
 * e só então fallback para dígitos.
 */
export const resolveClientDisplayName = (entry) => {
  if (!entry) return null;

  const contacts = collectContacts(entry);
  const knownPhones = new Set();

  contacts.forEach((contact) => {
    pushPhoneIfValid(knownPhones, contact?.whatsapp_number);
    pushPhoneIfValid(knownPhones, contact?.contact_phone);
    pushPhoneIfValid(knownPhones, contact?.phone_number);
    pushPhoneIfValid(knownPhones, contact?.phone);
    pushPhoneIfValid(knownPhones, contact?.whatsapp_jid);
    pushPhoneIfValid(knownPhones, contact?.whatsapp_lid);
  });

  pushPhoneIfValid(knownPhones, entry?.whatsapp_number);
  pushPhoneIfValid(knownPhones, entry?.whatsappNumber);
  pushPhoneIfValid(knownPhones, entry?.contact_phone);
  pushPhoneIfValid(knownPhones, entry?.phone_number);
  pushPhoneIfValid(knownPhones, entry?.phoneNumber);
  pushPhoneIfValid(knownPhones, entry?.phone);
  pushPhoneIfValid(knownPhones, entry?.rawWhatsappNumber);
  pushPhoneIfValid(knownPhones, entry?.jid);
  pushPhoneIfValid(knownPhones, entry?.remote_jid);
  pushPhoneIfValid(knownPhones, entry?.remoteJid);

  const savedCandidates = [];
  const notifyCandidates = [];
  const verifiedCandidates = [];
  const pushCandidates = [];
  const sharedCandidates = [];

  contacts.forEach((contact) => {
    pushIfValid(savedCandidates, contact?.name);
    pushIfValid(savedCandidates, contact?.contact_name);

    pushIfValid(notifyCandidates, contact?.notifyName);
    pushIfValid(notifyCandidates, contact?.notify_name);
    pushIfValid(notifyCandidates, contact?.whatsapp_name);
    pushIfValid(notifyCandidates, contact?.wa_contact_name);

    pushIfValid(verifiedCandidates, contact?.verifiedName);
    pushIfValid(verifiedCandidates, contact?.verified_name);
    pushIfValid(verifiedCandidates, contact?.verifiedBizName);
    pushIfValid(verifiedCandidates, contact?.verified_biz_name);

    pushIfValid(pushCandidates, contact?.push_name);
    pushIfValid(pushCandidates, contact?.pushName);
    pushIfValid(pushCandidates, contact?.profile_name);
    pushIfValid(pushCandidates, contact?.profileName);
    pushIfValid(pushCandidates, contact?.display_name);
    pushIfValid(pushCandidates, contact?.displayName);
    pushIfValid(pushCandidates, contact?.waPushName);
    pushIfValid(pushCandidates, contact?.baileys_contact_name);
  });

  pushIfValid(savedCandidates, entry?.name);
  pushIfValid(savedCandidates, entry?.customer_name);
  pushIfValid(savedCandidates, entry?.client_name);
  pushIfValid(savedCandidates, entry?.contact_name);

  pushIfValid(notifyCandidates, entry?.notifyName);
  pushIfValid(notifyCandidates, entry?.notify_name);
  pushIfValid(notifyCandidates, entry?.whatsapp_name);
  pushIfValid(notifyCandidates, entry?.wa_contact_name);

  pushIfValid(verifiedCandidates, entry?.verifiedName);
  pushIfValid(verifiedCandidates, entry?.verified_name);
  pushIfValid(verifiedCandidates, entry?.verifiedBizName);
  pushIfValid(verifiedCandidates, entry?.verified_biz_name);

  pushIfValid(pushCandidates, entry?.push_name);
  pushIfValid(pushCandidates, entry?.pushName);
  pushIfValid(pushCandidates, entry?.profile_name);
  pushIfValid(pushCandidates, entry?.profileName);
  pushIfValid(pushCandidates, entry?.display_name);
  pushIfValid(pushCandidates, entry?.displayName);
  pushIfValid(pushCandidates, entry?.waPushName);
  pushIfValid(pushCandidates, entry?.baileys_contact_name);

  if (entry?.client) {
    pushIfValid(savedCandidates, entry.client.name);
    pushIfValid(savedCandidates, entry.client.contact_name);
    pushIfValid(pushCandidates, entry.client.push_name);
    pushIfValid(pushCandidates, entry.client.profile_name);
  }

  if (entry?.customer) {
    pushIfValid(savedCandidates, entry.customer.name);
    pushIfValid(savedCandidates, entry.customer.contact_name);
    pushIfValid(pushCandidates, entry.customer.push_name);
    pushIfValid(pushCandidates, entry.customer.profile_name);
  }

  if (Array.isArray(entry?.messages)) {
    entry.messages.forEach((msg) => {
      pushIfValid(pushCandidates, msg?.push_name);
      pushIfValid(pushCandidates, msg?.profile_name);
      pushIfValid(pushCandidates, msg?.display_name);
      pushIfValid(sharedCandidates, msg?.sender_name);
      pushPhoneIfValid(knownPhones, msg?.sender_phone_digits);
    });
  }

  const orderedCandidates = [
    savedCandidates,
    notifyCandidates,
    verifiedCandidates,
    pushCandidates,
    sharedCandidates,
  ];

  for (const sourceCandidates of orderedCandidates) {
    const best = findBestCandidate(sourceCandidates, knownPhones);
    if (best) return best;
  }

  const fallbackPhone = Array.from(knownPhones).find(Boolean);
  return fallbackPhone || null;
};
import { getConversationContact } from './conversationContact.js';
